import { FC, useMemo, useCallback, useState } from 'react'
import { MixedAccountsTableItem } from 'selectors/linkageSelectors'
import { ProviderAccountNotification } from 'interfaces/linkage'
import Tooltip, { TooltipTheme } from 'components/features/pfm/Tooltip/Tooltip'
import { AccountStatusExtraMessage } from 'components/features/pfm/LinkedAccountTooltip/AccountStatusExtraMessage'
import { AccountTooltipAction } from 'components/features/pfm/LinkedAccountTooltip/AccountTooltipAction'
import { LinkedAccountBalanceNote } from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalanceNote'
import Icon, { IconKind } from 'components/atoms/Icon'
import styles from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalanceState.styl'
import cn from 'classnames'
import { INACTIVE_INSTITUTION_HINT } from 'constants/hints'
import { OuterEvent } from 'components/features/pfm/OuterEvent/OuterEvent'

const TOOLTIP_THEME_MAPPING: Record<ProviderAccountNotification, TooltipTheme> = {
  success: 'success',
  warning: 'warning',
  failure: 'danger',
  loader: 'info',
}

const ICON_MAPPING: Record<ProviderAccountNotification, IconKind> = {
  success: 'spinner-success',
  warning: 'spinner-warning',
  failure: 'spinner-error',
  loader: 'spinner-light',
}

type LinkedAccountBalanceStateProps = {
  accountInfo: MixedAccountsTableItem
  hideIcon?: boolean
}
export const LinkedAccountBalanceState: FC<LinkedAccountBalanceStateProps> = (props) => {
  const { accountInfo, hideIcon } = props
  const { providerAccountState, account } = accountInfo
  const { link_status = null } = account || {}

  const { extra_message, notification_type, message } = useMemo(() => {
    const { status } = providerAccountState
    if (status !== 'updating' && link_status === 'inactive' && (!providerAccountState.message || !providerAccountState.extra_message)) {
      return {
        notification_type: 'failure' as const,
        message: 'Additional information required',
        extra_message: INACTIVE_INSTITUTION_HINT,
      }
    }
    return providerAccountState
  }, [providerAccountState, link_status])

  const tooltipChild = useMemo(() => {
    if (!extra_message) {
      return null
    }

    const tooltipTheme = TOOLTIP_THEME_MAPPING[notification_type]

    return (
      <Tooltip
        theme={ tooltipTheme }
        light={ tooltipTheme === 'danger' }
        wrap
      >
        <div className={ styles.tooltipContent }>
          <AccountStatusExtraMessage message={ extra_message } />
          <div className={ styles.tooltipUpdate }>
            <AccountTooltipAction accountInfo={ accountInfo } />
          </div>
        </div>
      </Tooltip>
    )
  }, [extra_message, notification_type, accountInfo])

  const iconChild = useMemo(() => {
    const icon = ICON_MAPPING[notification_type]
    if (!icon || hideIcon) {
      return null
    }

    return (
      <div className={ styles.icon }>
        <Icon kind={ icon } />
      </div>
    )

  }, [notification_type, hideIcon])

  const noteChild = useMemo(() => {
    if (!message) {
      return null
    }
    return (
      <LinkedAccountBalanceNote>
        <div className={ cn(styles.note, styles[notification_type]) }>
          { iconChild }
          { message }
        </div>
      </LinkedAccountBalanceNote>
    )
  }, [iconChild, message, notification_type])

  const [tooltipShown, setTooltipShown] = useState(false)
  const handleClick = useCallback(() => {
    setTooltipShown(true)
  }, [])
  const handleClickOutside = useCallback(() => {
    setTooltipShown(false)
  }, [])

  if (!noteChild) {
    return null
  }

  if (!tooltipChild) {
    return noteChild
  }

  return (
    <OuterEvent
      onOuterTap={ handleClickOutside }
      onOuterClick={ handleClickOutside }
      onOuterESC={ handleClickOutside }
      onMouseLeave={ handleClickOutside }
    >
      <div className={ cn(styles.withTooltip, tooltipShown && styles.tooltipShown) } onClick={ handleClick }>
        { noteChild }
        <div className={ styles.tooltip }>
          { tooltipChild }
        </div>
      </div>
    </OuterEvent>
  )
}
